/**
 * UI styles (spec docs/specs/2026-10-08-v2-theme-styles-design.md §2, §6).
 *
 * A style is a token set (src/styles/tokens.css: Cosmos on :root, one
 * `:root[data-ui-style="…"]` block per other style) plus the recipe utilities the components
 * use. Components never branch on the style name; the only style-aware code is this resolver
 * and the boot (UiStyleContext), the font loader (src/styles/styleFonts.ts), the background art
 * gate and the dev switcher.
 *
 * Everything here is pure: callers pass the build value, the URL search string, storage and
 * whether the preview switcher is allowed (src/config/uiStyleEnv.ts).
 */

export const UI_STYLES = ['cosmos', 'arcade', 'daylight', 'luxe', 'dusk'] as const;
export type UiStyle = (typeof UI_STYLES)[number];
/** The style when VITE_UI_STYLE is missing or unknown (spec 2026-10-08-kiosk-web-split §7). */
export const DEFAULT_UI_STYLE: UiStyle = 'dusk';

/** Styles that draw the admin background art and particles; the others paint --k-page-background. */
export const STYLE_HAS_ART: Record<UiStyle, boolean> = {
  cosmos: true,
  arcade: true,
  daylight: false,
  luxe: false,
  dusk: false,
};

/** `?style=<name>` previews a style (switcher builds only); `?style=reset` clears the stored preview. */
export const UI_STYLE_PARAM = 'style';
/** The preview choice, kept for this browser. */
export const UI_STYLE_STORAGE_KEY = 'ui-style-preview';

type StyleStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export interface ResolveUiStyleInput {
  /** VITE_UI_STYLE (the style this build ships with). */
  buildStyle?: string;
  /** location.search of the page load. */
  search: string;
  storage: StyleStorage | null;
  /** DEV or VITE_UI_STYLE_SWITCHER: `?style=` and the stored preview apply. */
  switcherAllowed: boolean;
}

export function isUiStyle(value: unknown): value is UiStyle {
  return typeof value === 'string' && (UI_STYLES as readonly string[]).includes(value);
}

/** The style a build value names (VITE_UI_STYLE); missing or unknown means DEFAULT_UI_STYLE (Dusk). */
export function toUiStyle(value: unknown): UiStyle {
  return isUiStyle(value) ? value : DEFAULT_UI_STYLE;
}

function safeGet(storage: StyleStorage | null, key: string): string | null {
  try {
    return storage ? storage.getItem(key) : null;
  } catch {
    return null;
  }
}

function safeSet(storage: StyleStorage | null, key: string, value: string): void {
  try {
    storage?.setItem(key, value);
  } catch {
    /* private mode / quota */
  }
}

function safeRemove(storage: StyleStorage | null, key: string): void {
  try {
    storage?.removeItem(key);
  } catch {
    /* private mode */
  }
}

/** Stores a preview choice for this browser (the switcher and `?style=`). */
export function storeUiStylePreview(style: UiStyle, storage: StyleStorage | null): void {
  safeSet(storage, UI_STYLE_STORAGE_KEY, style);
}

/**
 * The style to render. The build value wins unless the preview switcher is allowed:
 * then a valid `?style=` applies (and is stored for this browser), else the stored preview.
 * `?style=reset` always clears the stored preview. Unknown names are ignored and never stored;
 * an unknown build value means DEFAULT_UI_STYLE (Dusk). When the switcher is not allowed, a stored preview is
 * neither applied nor deleted.
 */
export function resolveUiStyle({ buildStyle, search, storage, switcherAllowed }: ResolveUiStyleInput): UiStyle {
  const build = isUiStyle(buildStyle) ? buildStyle : DEFAULT_UI_STYLE;
  const param = new URLSearchParams(search).get(UI_STYLE_PARAM)?.trim().toLowerCase();
  if (param === 'reset') {
    safeRemove(storage, UI_STYLE_STORAGE_KEY);
    return build;
  }
  if (!switcherAllowed) return build;
  if (isUiStyle(param)) {
    storeUiStylePreview(param, storage);
    return param;
  }
  const stored = safeGet(storage, UI_STYLE_STORAGE_KEY);
  if (stored == null) return build;
  if (isUiStyle(stored)) return stored;
  safeRemove(storage, UI_STYLE_STORAGE_KEY);
  return build;
}

/** `<html data-ui-style>`: selects the style's token block in tokens.css. */
export function applyUiStyle(style: UiStyle, html: HTMLElement): void {
  html.dataset.uiStyle = style;
}
